import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  PlaceOutlined,
  ArrowBack,
  ChevronLeft,
  ChevronRight,
  Close,
  ContentCopyOutlined,
  CheckCircleOutlined,
  ShareOutlined,
  PhoneOutlined,
  MailOutlined,
  ImageNotSupportedOutlined,
} from "@mui/icons-material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { loadListing, loadPhotos } from "../api/publicApi";
import PhotoImage from "../../components/PhotoImage";
import {
  currencyNote,
  districtLabel,
  floorLabel,
  formatPrice,
  listingTitle,
  mapsUrl,
  parseInfo,
  relativeDate,
  roomsLabel,
  cleanStreet,
} from "../utils/format";
import { tokens, radius, shadow } from "../../theme/theme";
import { CONTACTS } from "../siteConfig";

/* ------------------------------------------------------------------ Gallery */

function Gallery({ photos, title }) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(false);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, photos]);

  if (!photos) {
    return <Skeleton variant="rounded" sx={{ borderRadius: radius.lg, aspectRatio: "16 / 10" }} />;
  }

  if (photos.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          aspectRatio: "16 / 10",
          borderRadius: radius.lg,
          backgroundColor: tokens.line2,
          color: tokens.ink400,
          gap: 1,
        }}
      >
        <ImageNotSupportedOutlined sx={{ fontSize: 40 }} />
        <Typography variant="body2">Фото цієї квартири ще готуються</Typography>
      </Stack>
    );
  }

  const go = (delta) => setIndex((i) => (i + delta + photos.length) % photos.length);

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          borderRadius: radius.lg,
          overflow: "hidden",
          backgroundColor: tokens.ink,
          aspectRatio: "16 / 10",
        }}
      >
        <Box
          component="img"
          src={photos[index].url}
          alt={title + " — фото " + (index + 1)}
          onClick={() => setZoom(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            cursor: "zoom-in",
          }}
        />
        {photos.length > 1 && (
          <React.Fragment>
            <IconButton
              aria-label="Попереднє фото"
              onClick={() => go(-1)}
              sx={navButtonSx({ left: 12 })}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              aria-label="Наступне фото"
              onClick={() => go(1)}
              sx={navButtonSx({ right: 12 })}
            >
              <ChevronRight />
            </IconButton>
            <Chip
              size="small"
              label={index + 1 + " / " + photos.length}
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
                backgroundColor: "rgba(14,27,46,0.72)",
                color: "#fff",
              }}
            />
          </React.Fragment>
        )}
      </Box>

      {photos.length > 1 && (
        <Stack
          direction="row"
          spacing={1}
          className="custom_scrollbar"
          sx={{ mt: 1.5, overflowX: "auto", pb: 0.5 }}
        >
          {photos.map((photo, i) => (
            <PhotoImage
              key={photo.key}
              src={photo.thumbUrl}
              fallbackSrc={photo.url}
              alt=""
              loading="lazy"
              onClick={() => setIndex(i)}
              sx={{
                width: 92,
                height: 68,
                flex: "0 0 auto",
                objectFit: "cover",
                borderRadius: radius.sm,
                cursor: "pointer",
                border: "2px solid " + (i === index ? tokens.blue600 : "transparent"),
                opacity: i === index ? 1 : 0.72,
                transition: "opacity .15s ease, border-color .15s ease",
                "&:hover": { opacity: 1 },
              }}
            />
          ))}
        </Stack>
      )}

      {zoom && (
        <Box
          onClick={() => setZoom(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            backgroundColor: "rgba(9,16,28,0.94)",
            display: "grid",
            placeItems: "center",
            p: { xs: 1, md: 4 },
          }}
        >
          <IconButton
            aria-label="Закрити"
            onClick={() => setZoom(false)}
            sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src={photos[index].url}
            alt=""
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain" }}
          />
          {photos.length > 1 && (
            <React.Fragment>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                sx={{ position: "absolute", left: 16, color: "#fff" }}
              >
                <ChevronLeft sx={{ fontSize: 34 }} />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                sx={{ position: "absolute", right: 16, color: "#fff" }}
              >
                <ChevronRight sx={{ fontSize: 34 }} />
              </IconButton>
            </React.Fragment>
          )}
        </Box>
      )}
    </Box>
  );
}

function navButtonSx(position) {
  return Object.assign(
    {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      backgroundColor: "rgba(255,255,255,0.9)",
      boxShadow: shadow.sm,
      "&:hover": { backgroundColor: "#fff" },
    },
    position
  );
}

/* ---------------------------------------------------------------- Spec rows */

function SpecRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      spacing={2}
      sx={{ py: 1.2 }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}

/* -------------------------------------------------------------------- Page  */

export default function ListingPage() {
  const params = useParams();
  const id = params.object_id || params.id;

  const [item, setItem] = useState(undefined);
  const [photos, setPhotos] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setItem(undefined);
    setPhotos(null);

    // The photo order lives on the listing, so the photos wait for it rather
    // than loading in parallel and then reshuffling under the reader.
    //
    // Deliberately NOT one chain: a failure to load photos must not be able to
    // report the listing itself as missing. Chaining them did exactly that —
    // one denied request and every flat on the site read "оголошення не
    // знайдено", which says nothing true about the listing.
    loadListing(id)
      .then((result) => {
        if (!alive) return;
        setItem(result);
        loadPhotos(id, result ? result.photoOrder : null)
          .then((photos) => {
            if (alive) setPhotos(photos);
          })
          .catch((err) => {
            console.error(err);
            if (alive) setPhotos([]);
          });
      })
      .catch((err) => {
        console.error(err);
        if (alive) {
          setItem(null);
          setPhotos([]);
        }
      });

    window.scrollTo(0, 0);
    return () => {
      alive = false;
    };
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    const title = item ? listingTitle(item) : "Квартира — LeoCity";
    if (navigator.share) {
      try {
        await navigator.share({ title: title, url: url });
        return;
      } catch (e) {
        /* user cancelled — fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.warn("Не вдалося скопіювати посилання", e);
    }
  };

  if (item === undefined) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Skeleton variant="text" width={260} height={40} />
        <Grid container spacing={4} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <Skeleton variant="rounded" sx={{ aspectRatio: "16 / 10", borderRadius: radius.lg }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: radius.lg }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (item === null) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Оголошення не знайдено
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Схоже, цю квартиру вже здали або посилання застаріло.
        </Typography>
        <Button component={RouterLink} to="/katalog" variant="contained" size="large">
          Перейти до каталогу
        </Button>
      </Container>
    );
  }

  const info = parseInfo(item.info);
  const title = listingTitle(item);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Breadcrumbs sx={{ mb: 2, fontSize: "0.85rem" }}>
        <Link component={RouterLink} to="/" color="inherit">
          Головна
        </Link>
        <Link component={RouterLink} to="/katalog" color="inherit">
          Каталог
        </Link>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Breadcrumbs>

      <Button
        component={RouterLink}
        to="/katalog"
        size="small"
        startIcon={<ArrowBack />}
        sx={{ mb: 2, display: { md: "none" } }}
      >
        Назад
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" }, mb: 1 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <PlaceOutlined sx={{ fontSize: 19, color: tokens.blue600 }} />
            <Typography color="text.secondary">
              Львів, {districtLabel(item.region)}
            </Typography>
            <Link href={mapsUrl(item)} target="_blank" rel="noopener noreferrer" sx={{ ml: 1 }}>
              На карті
            </Link>
          </Stack>
        </Box>

        <Button
          variant="outlined"
          startIcon={copied ? <CheckCircleOutlined /> : <ShareOutlined />}
          onClick={share}
          color={copied ? "success" : "primary"}
        >
          {copied ? "Скопійовано" : "Поділитися"}
        </Button>
      </Stack>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Gallery photos={photos} title={title} />

          {info.text && (
            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mt: 3, borderRadius: radius.lg }}>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                Опис
              </Typography>
              <Typography sx={{ whiteSpace: "pre-line", color: tokens.ink700 }}>
                {info.text}
              </Typography>
              {info.links.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 2 }}>
                  {info.links.map((url) => (
                    <Link key={url} href={url} target="_blank" rel="noopener noreferrer">
                      {url}
                    </Link>
                  ))}
                </Stack>
              )}
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={3} sx={{ position: { md: "sticky" }, top: { md: 96 } }}>
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: radius.lg, boxShadow: shadow.md }}
            >
              <Typography variant="overline" sx={{ color: tokens.ink400 }}>
                Орендна плата
              </Typography>
              <Typography variant="h2" sx={{ mt: 0.5, mb: currencyNote(item.currency) ? 0.5 : 2 }}>
                {formatPrice(item.cost, item.currency)}
              </Typography>
              {/*
                Realtors type the currency by hand and sometimes append a
                payment condition ("$ по курсу НБУ"). It is useful to the
                renter, so it is shown here rather than glued onto the number.
              */}
              {currencyNote(item.currency) && (
                <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                  Оплата {currencyNote(item.currency)}
                </Typography>
              )}

              <Divider />
              <SpecRow label="Кімнат" value={roomsLabel(item.rooms)} />
              <Divider />
              <SpecRow label="Поверх" value={floorLabel(item.floor)} />
              <Divider />
              <SpecRow label="Вулиця" value={cleanStreet(item.street)} />
              <Divider />
              <SpecRow label="Район" value={districtLabel(item.region)} />
              <Divider />
              <SpecRow label="Стан" value={item.state} />
              <Divider />
              <SpecRow label="Опалення" value={item.heating} />
              <Divider />
              <SpecRow label="Меблі" value={item.furniture ? "Є" : "Немає"} />
              <Divider />
              <SpecRow label="Побутова техніка" value={item.technology ? "Є" : "Немає"} />
              <Divider sx={{ mb: 2 }} />

              <Typography variant="caption">
                Оновлено {relativeDate(item.dateEdit || item.dateFrom)}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: radius.lg,
                background:
                  "linear-gradient(135deg, " + tokens.blue700 + ", " + tokens.blue900 + ")",
                color: "#fff",
              }}
            >
              <Typography variant="h4" sx={{ color: "#fff", mb: 1 }}>
                Хочете переглянути?
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mb: 2.5 }}>
                Зателефонуйте — рієлтор перевірить, чи квартира вільна, і
                домовиться про перегляд у зручний для вас час.
              </Typography>
              <Stack spacing={1.2}>
                {CONTACTS.phones.map((phone) => (
                  <Button
                    key={phone.href}
                    href={"tel:" + phone.href}
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    startIcon={<PhoneOutlined />}
                  >
                    {phone.label}
                  </Button>
                ))}
                <Button
                  href={
                    "mailto:" +
                    CONTACTS.email +
                    "?subject=" +
                    encodeURIComponent("Запит щодо оголошення " + item.id)
                  }
                  variant="outlined"
                  fullWidth
                  startIcon={<MailOutlined />}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.4)",
                    "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.08)" },
                  }}
                >
                  Написати на пошту
                </Button>
              </Stack>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 2 }}>
                <ContentCopyOutlined sx={{ fontSize: 15, opacity: 0.7 }} />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Код оголошення: {String(item.id).slice(0, 12)}
                </Typography>
              </Stack>
            </Paper>

            <Alert severity="info" sx={{ borderRadius: radius.md }}>
              Контакти власника не публікуємо — усі перегляди організовує агенція.
            </Alert>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}
